import api from './api';

export type ClaimCategory = 'product' | 'method' | 'programme' | 'storage_medium' | 'computer' | 'use';
export type ClaimableItemStatus = 'proposed' | 'accepted' | 'rejected';
export type RankingStatus = 'pending' | 'ranking' | 'ranked' | 'failed';

export interface ClaimableItem {
  id: string;
  label: string;
  category: ClaimCategory;
  linked_solution_ids: string[];
  notes: string | null;
  status: ClaimableItemStatus;
  ranking_status: RankingStatus;
  ranking_completed_at: string | null;
  expected_claims_count: number;
  created_at: string;
  last_modified_at: string;
  last_modified_by: string | null;
}

export interface ClaimableItemCreate {
  label: string;
  category: ClaimCategory;
  linked_solution_ids?: string[];
  notes?: string | null;
  status?: ClaimableItemStatus;
  expected_claims_count?: number;
}

export interface ClaimableItemUpdate {
  label?: string;
  category?: ClaimCategory;
  linked_solution_ids?: string[];
  notes?: string | null;
  status?: ClaimableItemStatus;
}

export interface SuggestedItem {
  label: string;
  category: ClaimCategory;
  notes: string | null;
  rationale: string;
}

export interface ScoredPatent {
  document_id: string;
  patent_id: string;
  title: string;
  score: number;
  rationale: string;
  excluded_by_user: boolean;
  scored_at: string;
}

export interface RankingProgress {
  status: 'idle' | 'running' | 'completed' | 'failed';
  items_total?: number;
  items_done?: number;
  items_completed?: number;
  current_item?: string;
  error?: string;
}

export interface RankingCostEstimate {
  items_to_rank: number;
  patent_count: number;
  estimated_llm_calls: number;
  tokens_per_call_input: number;
  tokens_per_call_output: number;
  estimated_cost_cents: number;
  currency: string;
  model_id: string;
  model_name: string | null;
  has_cache_note: boolean;
}

export interface RankRequest {
  item_ids?: string[] | null;
}

const base = (spId: string) => `/sub-projects/${spId}/claimable-items`;

export const claimableItemsService = {
  async list(subProjectId: string): Promise<ClaimableItem[]> {
    const r = await api.get<ClaimableItem[]>(`${base(subProjectId)}/`);
    return r.data;
  },

  async create(subProjectId: string, payload: ClaimableItemCreate): Promise<ClaimableItem> {
    const r = await api.post<ClaimableItem>(`${base(subProjectId)}/`, payload);
    return r.data;
  },

  async update(subProjectId: string, itemId: string, payload: ClaimableItemUpdate): Promise<ClaimableItem> {
    const r = await api.put<ClaimableItem>(`${base(subProjectId)}/${itemId}`, payload);
    return r.data;
  },

  async delete(subProjectId: string, itemId: string, force = false): Promise<unknown> {
    const r = await api.delete(`${base(subProjectId)}/${itemId}?force=${force}`);
    return r.data;
  },

  async suggest(
    subProjectId: string,
    solutionDescription: string,
    opts?: { productDescription?: string; problemStatement?: string; existingLabels?: string[] },
  ): Promise<SuggestedItem[]> {
    const r = await api.post<SuggestedItem[]>(`${base(subProjectId)}/suggest`, {
      solution_description: solutionDescription,
      product_description: opts?.productDescription ?? '',
      problem_statement: opts?.problemStatement ?? '',
      existing_labels: opts?.existingLabels ?? [],
    });
    return r.data;
  },

  async estimateRanking(subProjectId: string): Promise<RankingCostEstimate> {
    const r = await api.get<RankingCostEstimate>(`${base(subProjectId)}/rank-estimate`);
    return r.data;
  },

  async rankItems(subProjectId: string, itemIds?: string[]): Promise<void> {
    await api.post(`${base(subProjectId)}/rank`, { item_ids: itemIds ?? null });
  },

  async getRankingProgress(subProjectId: string): Promise<RankingProgress> {
    const r = await api.get<RankingProgress>(`${base(subProjectId)}/ranking-progress`);
    return r.data;
  },

  async getScoredPatents(subProjectId: string, itemId: string): Promise<ScoredPatent[]> {
    const r = await api.get<ScoredPatent[]>(`${base(subProjectId)}/${itemId}/scored-patents`);
    return r.data;
  },

  async togglePatentExclusion(subProjectId: string, itemId: string, docId: string): Promise<{ excluded_by_user: boolean }> {
    const r = await api.patch<{ excluded_by_user: boolean }>(
      `${base(subProjectId)}/${itemId}/scored-patents/${docId}`,
    );
    return r.data;
  },
};
